import React from "react";

const ReportCard = ({
  report,
  isOwner,
  onDelete,
  onToggleFavorite,
  onDuplicate,
  onRun,
  onEdit,
  isDeleting = false,
  isDuplicating = false,
}) => {
  const getReportTypeIcon = (type) => {
    switch (type) {
      case "roadmap":
        return "📅";
      case "data-quality":
        return "📊";
      case "relationship":
        return "🔗";
      default:
        return "📄";
    }
  };

  const getReportTypeLabel = (type) => {
    switch (type) {
      case "roadmap":
        return "Roadmap";
      case "data-quality":
        return "Data Quality";
      case "relationship":
        return "Relationship Analysis";
      default:
        return type;
    }
  };

  const handleRun = () => {
    if (onRun) {
      onRun(report.id);
    } else {
      // Placeholder for Phase 2
      alert(
        `Report execution will be available in Phase 2. Report ID: ${report.id}`
      );
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(report.id);
    } else {
      // Placeholder for Phase 2
      alert(
        `Report editing will be available in Phase 2. Report ID: ${report.id}`
      );
    }
  };

  return (
    <div
      className={`bg-gray-800 rounded-lg border border-gray-700 p-4 hover:border-gray-600 transition-colors ${
        isDeleting || isDuplicating ? "opacity-50 pointer-events-none" : ""
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="text-2xl">
            {getReportTypeIcon(report.report_type)}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">
              {report.name}
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              {getReportTypeLabel(report.report_type)}
              {report.entity_type && ` • ${report.entity_type}`}
            </p>
          </div>
        </div>
        <button
          onClick={() => onToggleFavorite(report.id)}
          className={`ml-2 p-1 rounded transition-colors ${
            report.is_favorite
              ? "text-yellow-400 hover:text-yellow-300"
              : "text-gray-500 hover:text-gray-400"
          }`}
          title={
            report.is_favorite ? "Remove from favorites" : "Add to favorites"
          }
        >
          <svg
            className="w-5 h-5"
            fill={report.is_favorite ? "currentColor" : "none"}
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            />
          </svg>
        </button>
      </div>

      {report.description && (
        <p className="text-sm text-gray-400 mb-3 line-clamp-2">
          {report.description}
        </p>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {report.category && (
          <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded">
            {report.category}
          </span>
        )}
        {report.tags && report.tags.length > 0 && (
          <>
            {report.tags.slice(0, 2).map((tag, idx) => (
              <span
                key={idx}
                className="px-2 py-1 bg-blue-900/50 text-blue-300 text-xs rounded"
              >
                {tag}
              </span>
            ))}
            {report.tags.length > 2 && (
              <span className="text-xs text-gray-500">
                +{report.tags.length - 2} more
              </span>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
        <span>
          {report.run_count || 0} run{report.run_count !== 1 ? "s" : ""}
        </span>
        {report.last_run_at && (
          <span>
            Last run: {new Date(report.last_run_at).toLocaleDateString()}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-gray-700">
        <button
          onClick={handleRun}
          className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
        >
          Run
        </button>

        {/* Edit button - only for owners */}
        {isOwner && (
          <button
            onClick={handleEdit}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors"
          >
            Edit
          </button>
        )}

        {/* Duplicate button - available for all users */}
        {onDuplicate && (
          <button
            onClick={() => onDuplicate(report.id)}
            disabled={isDuplicating || isDeleting}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={isDuplicating ? "Duplicating..." : "Duplicate to My Reports"}
          >
            {isDuplicating ? (
              <svg
                className="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            )}
          </button>
        )}

        {/* Delete button - only for owners */}
        {isOwner && onDelete && (
          <button
            onClick={() => onDelete(report.id)}
            disabled={isDeleting || isDuplicating}
            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={isDeleting ? "Deleting..." : "Delete"}
          >
            {isDeleting ? (
              <svg
                className="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default ReportCard;
